import { useEffect, useRef } from "react";
import { ApiError } from "@ferrivia/api-contract";
import { Screen, Text } from "@ferrivia/ui";
import { useTranslation } from "@ferrivia/i18n";
import { useApi, useTracker } from "./AuthProvider";
import type { SessionCredentials } from "./useSession";
import s from "./AuthCallback.module.css";

export interface AuthCallbackProps {
  onSignedIn: (credentials: SessionCredentials) => void;
  onFailed: (errorCode: string) => void;
}

export function AuthCallback({ onSignedIn, onFailed }: AuthCallbackProps) {
  const api = useApi();
  const tracker = useTracker();
  const { t } = useTranslation("auth");
  const started = useRef(false);

  useEffect(() => {
    if (started.current) return;
    started.current = true;

    const params = new URLSearchParams(window.location.search);
    const handoff = params.get("handoff");
    const errorCode = params.get("error");
    window.history.replaceState({}, "", "/");

    if (errorCode) {
      tracker.track("signin.failed", { code: errorCode });
      onFailed(errorCode);
      return;
    }
    if (!handoff) {
      tracker.track("signin.failed", { code: "invalid_state" });
      onFailed("invalid_state");
      return;
    }

    api
      .redeemHandoff({ code: handoff })
      .then((session) => {
        tracker.track("signin.callback_completed", {});
        onSignedIn({ token: session.token, expiresAt: session.expiresAt });
      })
      .catch((err) => {
        const code = err instanceof ApiError ? err.code : "handoff_expired";
        tracker.track("signin.failed", { code });
        onFailed(code);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onSignedIn, onFailed]);

  return (
    <Screen className={s.root}>
      <Text as="p">{t("callback.status", "Kirjaudutaan sisään...")}</Text>
    </Screen>
  );
}
